"use client"

import { useState, useRef, useCallback, useEffect } from "react"
import { trackEvent } from "@/lib/analytics"

export interface ChatMessage {
  id: string
  role: "user" | "assistant"
  content: string
  isStreaming?: boolean
}

export interface ChatError {
  message: string
  signupRequired?: boolean
  upgradeRequired?: boolean
}

const TOOL_LABELS: Record<string, string> = {
  search_knowledge_base: "Searching knowledge base",
  get_region_info: "Looking up region info",
  lookup_entity: "Looking up details",
  get_user_lawn_profile: "Loading your lawn profile",
  get_analysis_history: "Checking analysis history",
  get_reminders: "Fetching reminders",
  calculate_fertilizer: "Calculating fertilizer needs",
  calculate_watering: "Calculating watering schedule",
  calculate_mowing_aeration_seeding: "Calculating lawn care schedule",
  create_reminder: "Creating reminder",
}

export function getToolLabel(toolName: string): string {
  return TOOL_LABELS[toolName] || toolName.replace(/_/g, " ")
}

let messageIdCounter = 0
function generateId(): string {
  return `msg-${Date.now()}-${++messageIdCounter}`
}

export function useChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [activeTools, setActiveTools] = useState<string[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<ChatError | null>(null)
  const threadIdRef = useRef<string | undefined>(undefined)
  const abortRef = useRef<AbortController | null>(null)
  // Track conversation history for anonymous users (server can't persist)
  const historyRef = useRef<Array<{ role: "user" | "assistant"; content: string }>>([])

  // Typewriter drip queue — buffers incoming tokens and releases char-by-char
  // at a human-readable pace so users can read along as text appears.
  const dripQueueRef = useRef("")
  const dripTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dripTargetRef = useRef<string | null>(null) // assistantId being dripped
  // Optional callback fired when the queue drains naturally (used so the chat
  // is marked "done" only after the typewriter catches up to the server).
  const dripDrainCbRef = useRef<(() => void) | null>(null)

  const startDrip = useCallback((assistantId: string) => {
    if (dripTimerRef.current) return // already running
    dripTargetRef.current = assistantId
    const tick = () => {
      const queue = dripQueueRef.current
      if (queue.length === 0) {
        // Queue empty. If the server already signalled completion, fire the
        // drain callback and stop. Otherwise idle-poll for more tokens.
        if (dripDrainCbRef.current) {
          const cb = dripDrainCbRef.current
          dripDrainCbRef.current = null
          dripTimerRef.current = null
          dripTargetRef.current = null
          cb()
          return
        }
        dripTimerRef.current = setTimeout(tick, 16)
        return
      }

      const char = queue[0]
      dripQueueRef.current = queue.slice(1)

      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId ? { ...m, content: m.content + char } : m
        )
      )

      // Reading-paced delays. Target ~60 chars/sec with lighter pauses at
      // sentence/clause boundaries so the eye can land before the next phrase
      // without the stream feeling sluggish.
      const delay =
        ".!?".includes(char) ? 110
          : ",:;".includes(char) ? 50
            : char === "\n" ? 60
              : 14 + Math.random() * 10
      dripTimerRef.current = setTimeout(tick, delay)
    }
    dripTimerRef.current = setTimeout(tick, 16)
  }, [])

  // Called when the server stream is complete: keep dripping until the queue
  // empties, then run the callback. If nothing is queued, fire immediately.
  const awaitDripDrain = useCallback((cb: () => void) => {
    if (!dripTimerRef.current && dripQueueRef.current.length === 0) {
      cb()
      return
    }
    dripDrainCbRef.current = cb
  }, [])

  // Hard flush — used for error/abort cases where finishing the slow drip
  // would mean making the user wait through an incomplete or broken response.
  const flushDrip = useCallback(() => {
    if (dripTimerRef.current) {
      clearTimeout(dripTimerRef.current)
      dripTimerRef.current = null
    }
    const remaining = dripQueueRef.current
    const targetId = dripTargetRef.current
    if (remaining && targetId) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === targetId ? { ...m, content: m.content + remaining } : m
        )
      )
    }
    dripQueueRef.current = ""
    dripTargetRef.current = null
    dripDrainCbRef.current = null
  }, [])

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
      if (dripTimerRef.current) clearTimeout(dripTimerRef.current)
    }
  }, [])

  const clearError = useCallback(() => setError(null), [])

  const resetChat = useCallback(() => {
    abortRef.current?.abort()
    flushDrip()
    setMessages([])
    setActiveTools([])
    setIsStreaming(false)
    setError(null)
    threadIdRef.current = undefined
    historyRef.current = []
  }, [flushDrip])

  const sendMessage = useCallback(async (content: string) => {
    const trimmedContent = content.trim()
    if (!trimmedContent || isStreaming) return

    trackEvent('chat_message_sent', {
      message_length: trimmedContent.length,
      message_index: historyRef.current.length / 2,
      is_first_message: historyRef.current.length === 0,
      thread_id: threadIdRef.current,
      path: typeof window !== 'undefined' ? window.location.pathname : '',
    })

    setError(null)
    setActiveTools([])

    const userMsg: ChatMessage = {
      id: generateId(),
      role: "user",
      content: trimmedContent,
    }
    const assistantId = generateId()
    const priorHistory = historyRef.current

    setMessages((prev) => [
      ...prev,
      userMsg,
      { id: assistantId, role: "assistant", content: "", isStreaming: true },
    ])
    setIsStreaming(true)

    const controller = new AbortController()
    abortRef.current = controller

    try {
      const res = await fetch("/api/ai/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: trimmedContent,
          threadId: threadIdRef.current,
          history: priorHistory,
        }),
        signal: controller.signal,
      })

      if (res.status === 429) {
        const data = await res.json()
        setError({
          message: data.error || "Rate limit reached",
          signupRequired: data.signupRequired,
          upgradeRequired: data.upgradeRequired,
        })
        // Remove the empty assistant placeholder
        setMessages((prev) => prev.filter((m) => m.id !== assistantId))
        setActiveTools([])
        setIsStreaming(false)
        return
      }

      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: "Something went wrong" }))
        setError({ message: data.error || "Something went wrong" })
        setMessages((prev) => prev.filter((m) => m.id !== assistantId))
        setActiveTools([])
        setIsStreaming(false)
        return
      }

      const reader = res.body?.getReader()
      if (!reader) {
        setError({ message: "No response stream" })
        setActiveTools([])
        setIsStreaming(false)
        return
      }

      const decoder = new TextDecoder()
      let buffer = ""
      let fullAssistantContent = ""
      let historyCommitted = false
      const toolsUsed: string[] = []
      const turnStart = Date.now()

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split("\n\n")
        buffer = lines.pop() || ""

        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed.startsWith("data: ")) continue

          try {
            const event = JSON.parse(trimmed.slice(6))

            switch (event.type) {
              case "thread":
                threadIdRef.current = event.threadId
                break

              case "token":
                fullAssistantContent += event.content
                dripQueueRef.current += event.content
                startDrip(assistantId)
                break

              case "tool_start":
                toolsUsed.push(event.name)
                setActiveTools((prev) => [...prev, event.name])
                break

              case "tool_result":
                setActiveTools((prev) => prev.filter((t) => t !== event.name))
                break

              case "done":
                // Save assistant response to history for anonymous context
                historyRef.current = [
                  ...priorHistory,
                  { role: "user" as const, content: trimmedContent },
                  ...(fullAssistantContent
                    ? [{ role: "assistant" as const, content: fullAssistantContent }]
                    : []),
                ]
                historyCommitted = true
                trackEvent('chat_message_received', {
                  message_length: fullAssistantContent.length,
                  message_index: priorHistory.length / 2,
                  thread_id: threadIdRef.current,
                  tools_used: toolsUsed.join(',') || undefined,
                  tool_count: toolsUsed.length,
                  duration_ms: Date.now() - turnStart,
                  path: typeof window !== 'undefined' ? window.location.pathname : '',
                })
                setActiveTools([])
                // Wait for the typewriter to catch up before clearing the
                // streaming flag — otherwise the input unlocks and the cursor
                // disappears while text is still appearing.
                awaitDripDrain(() => {
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantId ? { ...m, isStreaming: false } : m
                    )
                  )
                  setIsStreaming(false)
                })
                break

              case "error":
                flushDrip()
                setError({ message: event.message || "Agent error" })
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantId ? { ...m, isStreaming: false } : m
                  )
                )
                setActiveTools([])
                setIsStreaming(false)
                break
            }
          } catch {
            // skip malformed lines
          }
        }
      }

      // If stream ended without a done event, drain the queue first so the
      // user still sees the response type out at human pace.
      if (!historyCommitted) {
        // Do not commit failed/incomplete turns into anonymous context.
        historyRef.current = priorHistory
      }
      setActiveTools([])
      awaitDripDrain(() => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, isStreaming: false } : m
          )
        )
        setIsStreaming(false)
      })
    } catch (err: unknown) {
      flushDrip()
      if (err instanceof DOMException && err.name === "AbortError") return
      setError({ message: "Connection failed. Please try again." })
      setMessages((prev) => prev.filter((m) => m.id !== assistantId))
      historyRef.current = priorHistory
      setActiveTools([])
      setIsStreaming(false)
    }
  }, [isStreaming, flushDrip, startDrip, awaitDripDrain])

  return {
    messages,
    activeTools,
    isStreaming,
    error,
    sendMessage,
    clearError,
    resetChat,
  }
}
